<script setup lang="ts">
import AppState, { getNestedCustomValues } from '@/states/app'
import { InfoCircleIcon, Trash03Icon } from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIFormItem,
  UIInput,
  UIInputGroup,
  UISelect,
  UITooltip,
} from '@gohighlevel/ghl-ui'
import { computed, h, reactive, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vue-toastification'
import {
  CalendarType,
  LocationConfigurations,
  TeamMember,
} from '../../models/calendar'
import {
  handleNestedCustomValueModification,
  meetingLocationFieldOptionsEvent,
} from '../../utils/customValueMapper'
import { getTooltip } from '../../utils/generalFunctions'
import { hasDuplicateKeyValue, hasEmptyKeys } from '../../utils/validation'
import { filterMeetingRoomOptions } from '@/utils/meetingRoomVisibility'
import HeaderTile from '../common/HeaderTile.vue'
import CustomVariablePickerV2 from '../dynamic/CustomVariablePickerV2.vue'
import TeamMemberPicker from './TeamMemberPicker.vue'

const teamMemberPicker = ref()
const isAliasFieldEnabled = ref<number[]>([])
const { t } = useI18n()
const toast = useToast()
const locationError = ref('')

const meetingLocationNew = ref({
  custom: '',
  booker: '',
  inbound_call: '{{location.phone}}',
  outbound_call: '{{location.phone}}',
  physical: '{{location.full_address}}',
})

const meetingLocationTypesEvent = computed(() => {
  const options = [
    {
      label: t('calendar_advanced.meeting_location.custom'),
      value: 'custom',
    },
    {
      label: t('calendar_advanced.meeting_location.lc_meet'),
      value: 'lc_meet_conference',
      disabled: true,
    },
    {
      label: t('calendar_advanced.meeting_location.phone'),
      value: 'inbound_call',
    },
    {
      label: t('calendar_advanced.meeting_location.full_address'),
      value: 'physical',
    },
    {
      label: t('calendar_advanced.meeting_location.ask_the_booker'),
      value: 'booker',
    },
    {
      label: t('calendar_advanced.meeting_location.zoom'),
      value: 'zoom_conference',
      disabled: true,
    },
    {
      label: t('calendar_advanced.meeting_location.google_meet'),
      value: 'google_conference',
      disabled: true,
    },
    {
      label: t('calendar_advanced.meeting_location.microsoft_teams'),
      value: 'ms_teams_conference',
      disabled: true,
    },
  ]
  const visibleOptions = filterMeetingRoomOptions(
    options,
    AppState.isMeetingRoomEnabled,
    state.formData.locationConfigurations.map(config => config.kind)
  )
  const enabledOptions = visibleOptions.filter(option => !option.disabled)
  const disabledOptions = visibleOptions.filter(option => option.disabled)

  if (disabledOptions.length > 0) {
    return [
      {
        type: 'group',
        label: `Supported Meeting Locations`,
        key: 'enabled-options',
        children: enabledOptions,
      },
      {
        type: 'group',
        label: t('calendar_advanced.meeting_details.not_supported_event'),
        key: 'disabled-options',
        children: disabledOptions,
      },
    ]
  }
  return options
})

const editingCalendar = computed(() => {
  return AppState.editingCalendar
})

const state = reactive({
  collectiveAddress: '' as string | undefined,
  selectedTeamMembers: [] as TeamMember[],
  formData: {
    meetingLocation: '',
    locationConfigurations: [
      {
        kind: 'custom',
        location: '',
        position: 0,
        meetingId: 'custom_0',
      },
    ] as LocationConfigurations[],
  },
  enableSameUserAssignment: false,
  enableSameUserAssignmentForReschedule: false,
})

const MEETING_LOCATION_TYPES = ref({
  custom: t('calendar_advanced.meeting_details.custom'),
  zoom_conference: 'Zoom',
  ms_teams_conference: 'Microsoft Teams',
  google_conference: 'Google Meet',
  lc_meet_conference: t('calendar_advanced.meeting_location.lc_meet'),
  inbound_call: 'Phone',
  outbound_call: 'Phone Outgoing',
  physical: 'Full Address',
})

const collectiveMeetingLocations = computed(() => {
  return state.selectedTeamMembers?.map((t: any) => {
    const user = (AppState.globalUsers as any).users.find(
      (u: any) => u.id === t.userId
    )

    const locationConfig: LocationConfigurations = t
      ?.locationConfigurations?.[0] || {
      kind: 'custom',
      location: '',
      position: 0,
    }

    return {
      value: t.userId,
      label: `${user?.name}  (${
        MEETING_LOCATION_TYPES.value[locationConfig.kind] || 'Custom'
      }) `,
    }
  })
})

const meetingLocationEventRef = ref(meetingLocationFieldOptionsEvent)

// Function to load and update custom values with nested folder structure
const updateCustomValuesRefs = async () => {
  // Set base options first (synchronous)
  meetingLocationEventRef.value = meetingLocationFieldOptionsEvent

  // Then fetch and add custom values (asynchronous)
  const nestedCustomValues = await getNestedCustomValues()
  const customValues = handleNestedCustomValueModification(nestedCustomValues)

  if (customValues) {
    meetingLocationEventRef.value = [
      ...meetingLocationFieldOptionsEvent,
      customValues,
    ]
  }
}

const methods = {
  async loadData() {
    state.formData.meetingLocation =
      editingCalendar.value?.meetingLocation || ''
    state.formData.locationConfigurations = editingCalendar.value
      ?.locationConfigurations?.length
      ? (editingCalendar.value
          ?.locationConfigurations as LocationConfigurations[])
      : [
          {
            kind: 'custom',
            location: editingCalendar.value?.meetingLocation || '',
            position: 0,
            meetingId: 'custom_0',
          },
        ]

    if (editingCalendar.value?.calendarType === CalendarType.COLLECTIVE) {
      state.selectedTeamMembers = editingCalendar.value?.teamMembers
      state.collectiveAddress = state.selectedTeamMembers.find(
        x => x.useMeetingForCollective
      )?.userId
    }

    state.enableSameUserAssignment =
      editingCalendar.value?.enableSameUserAssignment || false
    state.enableSameUserAssignmentForReschedule =
      editingCalendar.value?.enableSameUserAssignmentForReschedule || false
  },
  updateSelectedTeamMember(teamMembers) {
    state.selectedTeamMembers = teamMembers
    AppState.editCalendarValidationData.teamMembers = teamMembers
    const selectedTeamMember = state.selectedTeamMembers.find(
      x => x.userId === state.collectiveAddress
    )
    state.collectiveAddress = selectedTeamMember ? state.collectiveAddress : ''
  },
  updateAdvancedSchedulingSettings(settings) {
    state.enableSameUserAssignment = settings.enableSameUserAssignment
    state.enableSameUserAssignmentForReschedule =
      settings.enableSameUserAssignmentForReschedule
  },
  openAliasField(index: number) {
    isAliasFieldEnabled.value.push(index)
  },
  closeAliasField(index: number) {
    isAliasFieldEnabled.value = isAliasFieldEnabled.value.filter(
      x => x !== index
    )
  },
  handleEventLocationTypeChange(value: string, index: number) {
    locationError.value = ''
    if (
      value === 'booker' &&
      state.formData.locationConfigurations.findIndex(
        item => item.kind === 'booker'
      ) > -1
    ) {
      state.formData.locationConfigurations.splice(index, 1)
      locationError.value = t(
        'calendar_advanced.meeting_details.ask_the_booker_error_message'
      )
      return
    }
    state.formData.locationConfigurations[index].location =
      meetingLocationNew.value[value]
  },
  getLocationData() {
    return {
      locationConfigurations: state.formData.locationConfigurations,
      collectiveAddress: state.collectiveAddress,
      selectedTeamMembers: state.selectedTeamMembers,
      enableSameUserAssignment: state.enableSameUserAssignment,
      enableSameUserAssignmentForReschedule:
        state.enableSameUserAssignmentForReschedule,
    }
  },
  async emitSave() {
    locationError.value = ''
    if (
      editingCalendar.value?.calendarType === CalendarType.EVENT &&
      hasEmptyKeys(state.formData?.locationConfigurations)
    ) {
      locationError.value = t('validation_messages.meeting_location_empty')
      return
    }
    if (
      editingCalendar.value?.calendarType === CalendarType.EVENT &&
      hasDuplicateKeyValue(state.formData?.locationConfigurations)
    ) {
      locationError.value = t('validation_messages.meeting_location_duplicate')
      return
    }
    const payload = {
      enableSameUserAssignment:
        AppState.editCalendarValidationData.enableSameUserAssignment,
      enableSameUserAssignmentForReschedule:
        state.enableSameUserAssignmentForReschedule,
    } as Record<string, any>

    if (editingCalendar.value?.calendarType === CalendarType.EVENT) {
      payload.meetingLocation = ''
      payload.locationConfigurations =
        state.formData?.locationConfigurations.map(x => {
          return {
            kind: x?.kind,
            location: x?.location,
            position: x?.position,
            meetingId: x?.kind + '_' + x?.position,
            ...(x?.alias && { alias: x?.alias }),
          }
        })
    } else {
      payload.teamMembers =
        await teamMemberPicker.value.methods.getTeamMembers()
      if (!payload.teamMembers) {
        return null
      }

      if (editingCalendar.value?.calendarType === CalendarType.COLLECTIVE) {
        payload.teamMembers = payload.teamMembers.map((x: TeamMember) => {
          x.useMeetingForCollective = x.userId === state.collectiveAddress
          return x
        })
      }

      payload.eventType = teamMemberPicker.value.methods.getEventType()

      if (payload.teamMembers.length > 1) {
        payload.enableRecurring = false
      }
    }

    return payload
  },
}

defineExpose({
  methods,
})

// Watch only when calendar ID changes to avoid unnecessary API calls
watch(
  () => editingCalendar.value?.id,
  newId => {
    if (newId) {
      updateCustomValuesRefs()
    }
  },
  { immediate: true }
)

watch(
  () => editingCalendar.value,
  () => {
    methods.loadData()
  },
  { immediate: true }
)

watch(
  () => state.formData.locationConfigurations,
  () => {
    locationError.value = ''
  },
  { deep: true }
)

watchEffect(() => {
  AppState.isMultipleLocationEnabled =
    state.formData.locationConfigurations.length > 1
})

const insertEventLocationConfig = (value: string, index: number) => {
  state.formData.locationConfigurations[index].location =
    state.formData.locationConfigurations[index].location + ' ' + value
}

const commonStyles = {
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '20px',
}

const renderLabel = (option, selected) => {
  return h(
    'div',
    {
      style: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        marginTop:
          option.type === 'group' && option.key === 'disabled-options'
            ? '5px'
            : '0',
      },
    },
    [
      option.type === 'group' && option.key === 'disabled-options'
        ? h('div', {
            style: {
              height: '1px',
              backgroundColor: 'var(--gray-300)',
              marginBottom: '8px',
              width: '100%',
              flexShrink: 0,
              alignSelf: 'stretch',
            },
          })
        : null,
      h(
        'div',
        {
          style: {
            ...commonStyles,
            width: '100%',
          },
          class: selected ? 'bg-primary' : '',
        },
        [
          h(
            'p',
            {
              style: {
                margin: 0,
                width: '100%',
              },
            },
            [option.label as string]
          ),
          option.description &&
            h(
              'span',
              {
                style: {
                  color: 'var(--gray-500)',
                },
              },
              option.description
            ),
        ]
      ),
    ]
  )
}

const headerClass = computed(() => {
  return editingCalendar?.value?.isRoundRobinCalendar || editingCalendar?.value?.isEventCalendar ? 'my-[15px]' : 'mt-[15px]'
})
</script>

<template>
  <div class="rounded-xl border bg-white p-5 !pr-[18px] shadow-sm">
    <HeaderTile
      :header="
        editingCalendar?.isEventCalendar
          ? $t('calendar_advanced.meeting_details.meeting_location')
          : $t('calendar_advanced.meeting_details.team_member_meeting_location')
      "
      :description="
        editingCalendar?.isEventCalendar
          ? $t(
              'calendar_advanced.meeting_details.event_calendar_meeting_location_desc'
            )
          : $t('calendar_advanced.meeting_details.meeting_location_desc')
      "
    />
    <div class="border-t border-gray-200"
    
    :class="headerClass"
     />
    <UIFormItem
      v-if="editingCalendar?.isEventCalendar"
      :label="$t('calendar_advanced.meeting_details.meeting_location_label')"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_location_tooltip')
        )
      "
    >
      <div class="w-full">
        <div
          v-for="(_, index) in state.formData.locationConfigurations"
          :key="index"
          class="mb-4 w-full gap-3"
        >
          <div class="flex w-full gap-3">
            <div class="flex flex-col gap-1 w-1/2">
              <UISelect
                id="calendar-event-mutli-select"
                v-model:value="
                  state.formData.locationConfigurations[index].kind
                "
                :options="meetingLocationTypesEvent"
                class="w-full"
                :render-label="renderLabel"
                @change="(value: string) => methods.handleEventLocationTypeChange(value, index)"
              />
              <div
                v-if="
                  state.formData.locationConfigurations[index].kind === 'booker'
                "
                class="text-sm text-gray-400 mt-[5px]"
              >
                {{
                  $t('calendar_advanced.meeting_location.ask_the_booker_desc')
                }}
              </div>
            </div>
            <div class="flex flex-1 flex-col gap-3">
              <div class="flex flex-1 gap-3">
                <UIInput
                  v-if="
                    ['inbound_call', 'physical'].includes(
                      state.formData.locationConfigurations[index].kind
                    )
                  "
                  id="calendar-event-title-input"
                  v-model="
                    state.formData.locationConfigurations[index].location
                  "
                  :placeholder="
                    $t('calendar_advanced.meeting_details.enter_location')
                  "
                  class="flex-1"
                />
                <UIInputGroup
                  v-if="
                    state.formData.locationConfigurations[index].kind ===
                    'custom'
                  "
                  v-bind="{}"
                  style="max-width: 400px"
                  class="flex-1"
                >
                  <UIInput
                    v-model="
                      state.formData.locationConfigurations[index].location
                    "
                    :placeholder="
                      $t('calendar_advanced.meeting_details.enter_location')
                    "
                    class="flex-1"
                  >
                    <template #suffix>
                      <CustomVariablePickerV2
                        :dropdown-options="meetingLocationEventRef"
                        @update:selected-value="
                          val => insertEventLocationConfig(val, index)
                        "
                      />
                    </template>
                  </UIInput>
                  <UIButton
                    class="h-full"
                    :disabled="
                      isAliasFieldEnabled.includes(index) ||
                      state.formData.locationConfigurations[index].alias?.length
                    "
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="text-primary-600"
                        @click="
                          () => {
                            if (
                              !(
                                isAliasFieldEnabled.includes(index) ||
                                state.formData.locationConfigurations[index]
                                  .alias?.length
                              )
                            ) {
                              methods.openAliasField(index)
                            }
                          }
                        "
                      >
                        {{
                          $t(
                            'calendar_advanced.meeting_details.add_display_label'
                          )
                        }}
                      </span>
                      <UITooltip>
                        <template #trigger>
                          <InfoCircleIcon class="h-4 w-4 text-primary-600" />
                        </template>
                        <p class="max-w-[350px]">
                          {{
                            $t(
                              'calendar_advanced.meeting_details.add_display_label_tooltip'
                            )
                          }}
                        </p>
                      </UITooltip>
                    </div>
                  </UIButton>
                </UIInputGroup>
                <UIButton
                  id="remove-location"
                  class="h-10 w-10 rounded-lg bg-gray-100 text-black"
                  :text="true"
                  aria-labelledby="remove-location"
                  @click="
                    state.formData.locationConfigurations.splice(index, 1),
                      methods.closeAliasField(index)
                  "
                >
                  <Trash03Icon class="w-4" />
                </UIButton>
              </div>
              <div
                v-if="
                  isAliasFieldEnabled.includes(index) ||
                  state.formData.locationConfigurations[index].alias
                "
                class="flex flex-1 gap-3"
              >
                <UIInput
                  v-model="state.formData.locationConfigurations[index].alias"
                  :placeholder="
                    $t('calendar_advanced.meeting_details.enter_display_label')
                  "
                />
                <UIButton
                  id="remove-location"
                  class="h-10 w-10 rounded-lg bg-gray-100 text-black"
                  :text="true"
                  aria-labelledby="remove-location"
                  @click="
                    methods.closeAliasField(index),
                      (state.formData.locationConfigurations[index].alias = '')
                  "
                >
                  <Trash03Icon class="w-4" />
                </UIButton>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="locationError"
          class="pb-3 text-[14px] text-red-500"
        >
          {{ locationError }}
        </div>

        <div class="flex items-center gap-2">
          <UIButton
            :id="'add-location-btn'"
            :type="'tertiary'"
            :size="'medium'"
            :disabled="false"
            :loading="false"
            :ghost="false"
            :quaternary="false"
            :circle="false"
            :text="true"
            :link-gray="undefined"
            :style="{ backgroundColor: '#EFF4FF' }"
            class="rounded px-4 py-3"
            @click="
              state.formData.locationConfigurations.push({
                kind: 'custom',
                location: '',
                position: state.formData.locationConfigurations.length,
                meetingId:
                  'custom' + '_' + state.formData.locationConfigurations.length,
              })
            "
          >
            {{ $t('calendar_advanced.meeting_details.add_location') }}
          </UIButton>
          <UITooltip>
            <template #trigger>
              <InfoCircleIcon class="h-4 w-4 text-primary-600" />
            </template>
            <p class="max-w-[350px]">
              {{
                $t(
                  'calendar_advanced.meeting_details.multiple_location_support_message'
                )
              }}
            </p>
          </UITooltip>
        </div>
      </div>
    </UIFormItem>

    <TeamMemberPicker
      ref="teamMemberPicker"
      @on-team-member-select="methods.updateSelectedTeamMember"
      @on-advanced-scheduling-settings-update="
        methods.updateAdvancedSchedulingSettings
      "
    />
    <UIFormItem
      v-if="editingCalendar?.calendarType === CalendarType.COLLECTIVE"
      class="mt-[20px]"
      :label="$t('calendar_advanced.meeting_details.meeting_location_label')"
      :tooltip="
        getTooltip(
          $t('calendar_advanced.meeting_details.meeting_location_tooltip')
        )
      "
    >
      <UISelect
        id="calendar-collective-address-select"
        v-model:value="state.collectiveAddress"
        :options="collectiveMeetingLocations"
      />
    </UIFormItem>
  </div>
</template>
